import { useState, FormEvent } from 'react';

interface Session {
  pseudo: string;
  pseudoDisplay: string;
  compet: boolean;
  token?: string;
  hashPass?: string;
}

interface OAuthLinkExistingProps {
  sessionId: string;
  canLink: boolean;
  pseudoCount: number;
  maxPseudos: number;
  providerLabel?: string;
  onLogin: (session: Session) => void;
  onError: (message: string) => void;
  compact?: boolean;
}

import { BACKEND_URL } from '../config';

/**
 * Formulaire de rattachement d'un compte WebTarot existant (pseudo + mot de passe)
 * à l'adresse email du compte OAuth (Google, Facebook, etc.).
 */
export function OAuthLinkExisting({
  sessionId,
  canLink,
  pseudoCount,
  maxPseudos,
  providerLabel = 'votre compte',
  onLogin,
  onError,
  compact = false,
}: OAuthLinkExistingProps) {
  const [linkPseudo, setLinkPseudo] = useState('');
  const [linkPassword, setLinkPassword] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const handleLinkExisting = async (e: FormEvent) => {
    e.preventDefault();
    onError('');
    setLinkLoading(true);

    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/oauth/link-existing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          pseudo: linkPseudo.trim(),
          password: linkPassword,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        const messages: Record<string, string> = {
          invalid_session: 'Session expirée. Veuillez recommencer la connexion.',
          session_expired: 'Session expirée. Veuillez recommencer la connexion.',
          invalid_credentials: 'Pseudo ou mot de passe incorrect.',
          account_disabled: 'Ce compte n\'est pas disponible.',
          email_limit_reached: `Maximum ${maxPseudos} pseudos par adresse email.`,
          email_required: 'Adresse email requise pour rattacher un compte.',
        };
        throw new Error(messages[data.error] || data.message || 'Rattachement impossible');
      }

      onLogin({
        pseudo: data.user.pseudo,
        pseudoDisplay: data.user.pseudo_display || data.user.pseudo,
        compet: false,
        token: data.token,
        hashPass: data.hashPass || data.hash_pass,
      });
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Rattachement impossible');
    } finally {
      setLinkLoading(false);
    }
  };

  if (!canLink) {
    return (
      <p className="oauth-link-limit">
        Limite atteinte : {maxPseudos} pseudos maximum pour cette adresse email ({pseudoCount}/{maxPseudos}).
      </p>
    );
  }

  return (
    <div className="oauth-link-section">
      <div className="oauth-link-header">
        <h3 className="oauth-link-title">
          {compact ? 'Un autre compte Webtarot ?' : 'Vous avez déjà un compte Webtarot ?'}
        </h3>
        <button
          type="button"
          className="oauth-link-expand-btn"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          {expanded ? 'Masquer' : 'Cliquez pour le rattacher'}
        </button>
      </div>

      {expanded && (
        <>
          <p className="oauth-link-desc">
            Saisissez votre pseudo et mot de passe : l&apos;adresse email de ce compte sera mise à jour
            pour correspondre à votre compte {providerLabel}.
            {pseudoCount > 0 && (
              <span className="oauth-link-count"> ({pseudoCount}/{maxPseudos} comptes liés)</span>
            )}
          </p>
          <form onSubmit={handleLinkExisting} className="login-form oauth-link-form">
            <div className="form-group">
              <label htmlFor={compact ? 'link-pseudo-compact' : 'link-pseudo'}>Pseudo</label>
              <input
                type="text"
                id={compact ? 'link-pseudo-compact' : 'link-pseudo'}
                value={linkPseudo}
                onChange={(e) => setLinkPseudo(e.target.value)}
                placeholder="Votre pseudo actuel"
                autoComplete="username"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor={compact ? 'link-password-compact' : 'link-password'}>Mot de passe</label>
              <input
                type="password"
                id={compact ? 'link-password-compact' : 'link-password'}
                value={linkPassword}
                onChange={(e) => setLinkPassword(e.target.value)}
                placeholder="Mot de passe Webtarot"
                autoComplete="current-password"
                required
              />
            </div>
            <button
              type="submit"
              className="login-button login-button-secondary"
              disabled={linkLoading || !linkPseudo.trim() || !linkPassword}
            >
              {linkLoading ? 'Rattachement...' : 'Rattacher ce compte'}
            </button>
          </form>
        </>
      )}
    </div>
  );
}
